import { IAuction, IAuctionBid } from '@/types'
import routes from 'constants/routes'
import dayjs from 'dayjs'
import { FlatCollectionType } from 'utils/data'

export function parseAuctionUrl(slug: string | string[]) {
  const slugParts = typeof slug === 'string' ? slug.split('/') : slug

  const artistName = slugParts.at(-3)
  const artworkName = slugParts.at(-2)
  const id = Number(slugParts.at(-1)) || undefined

  return { artistName, artworkName, id }
}

export function determineAuctionStatus(auction: IAuction['attributes']) {
  const currentDate = dayjs(undefined, { utc: true })
  const startDate = dayjs(auction.startDate, { utc: true })
  const endDate = dayjs(auction.endDate, { utc: true })

  const isPending = currentDate.isBefore(startDate, 'milliseconds')
  const isStarted =
    currentDate.isAfter(startDate, 'milliseconds') &&
    currentDate.isBefore(endDate, 'milliseconds')
  const isEnded = currentDate.isAfter(endDate, 'milliseconds')

  return {
    pending: isPending,
    started: isStarted,
    ended: isEnded,
    startDate,
    endDate,
  }
}

export function determineAuctionHighestBid(
  auction: IAuction['attributes'] | undefined | null
) {
  if (auction?.highestBid?.data) {
    return {
      price: auction.highestBid.data?.attributes.price,
      currency: auction.highestBid.data?.attributes.currency,
    }
  }

  if (auction?.startPrice && auction.currency) {
    return {
      price: auction.startPrice,
      currency: auction.currency,
    }
  }

  return {
    price: null,
    currency: null,
  }
}

function determineNextBidIncrement(price: number) {
  let increment = 1_000

  if (price < 50_000) increment = 1_000
  else if (price < 100_000) increment = 2_000
  else if (price < 200_000) increment = 5_000
  else if (price < 500_000) increment = 10_000
  else if (price < 1_000_000) increment = 25_000
  else increment = 50_000

  return increment
}

/**
 * price options should go up in the following increments, depending on the highest bid:
 * below $50k --> $1k increments
 * $50k - $100k -->$2k increments
 * $100k - $200k --> $5k increments
 * $200k - $500k --> $10k increments
 * $500k --> $25k increments
 * User should be able to scroll all the way to $750k
 */
export function getBidPriceOptions(highestBid: number) {
  if (!highestBid) return []

  const maxPrice = 1_500_000
  const currentPrice = highestBid
  const options: number[] = []
  let increment = determineNextBidIncrement(highestBid)
  let nextPrice = Math.ceil(currentPrice / increment) * increment + increment

  while (nextPrice <= maxPrice) {
    options.push(nextPrice)
    nextPrice += increment
    increment = determineNextBidIncrement(nextPrice)
  }

  return options
}

export function getAuctionUrl(slug: string, id: number) {
  return `${routes.auctions}/${slug}/${id}`
}

export function getAuctionPlaceBidUrl(step: string, id: number) {
  return `${routes.auctionPlaceBid.base}/${step}/${id}`
}

export function isHighestBidder(
  highestBid: FlatCollectionType<IAuctionBid> | undefined | null,
  userBid: FlatCollectionType<IAuctionBid> | undefined | null
) {
  if (!highestBid || !userBid) return false

  return Number(highestBid?.price) === Number(userBid?.price)
}

export function isOutbid(
  highestBid: FlatCollectionType<IAuctionBid> | undefined | null,
  userBid: FlatCollectionType<IAuctionBid> | undefined | null
) {
  if (!highestBid?.price || !userBid?.price) return false

  return Number(highestBid?.price) > Number(userBid?.price)
}

export function isPendingApproval(
  bid: FlatCollectionType<IAuctionBid> | undefined | null
) {
  return bid?.status === 'Pending_Approval'
}
